import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import type { SettingsData, SettingsResponse } from '@tekvoice/shared';
import { useAuth } from '@/auth/auth-context';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export const SETTINGS_QUERY_KEY = ['settings'] as const;

export function useSettingsQuery(): ReturnType<typeof useQuery<SettingsResponse>> {
  return useQuery({ queryKey: SETTINGS_QUERY_KEY, queryFn: () => api.settings.get() });
}

/**
 * Keeps a local draft of one settings section, seeded from the server document and
 * re-seeded after a save. EMPLOYEEs can read settings but not change them (SPEC §9),
 * so every control takes `disabled` from the permission matrix.
 */
export function useSettingsDraft<K extends keyof SettingsData>(
  section: K,
): {
  draft: SettingsData[K] | null;
  setDraft: React.Dispatch<React.SetStateAction<SettingsData[K] | null>>;
  serverValue: SettingsData[K] | undefined;
  isLoading: boolean;
  readOnly: boolean;
  dirty: boolean;
} {
  const { can } = useAuth();
  const settings = useSettingsQuery();
  const serverValue = settings.data?.data[section];
  const [draft, setDraft] = React.useState<SettingsData[K] | null>(null);

  React.useEffect(() => {
    if (serverValue !== undefined && draft === null) setDraft(serverValue);
  }, [serverValue, draft]);

  return {
    draft,
    setDraft,
    serverValue,
    isLoading: settings.isLoading,
    readOnly: !can('settings.manage'),
    dirty: draft !== null && JSON.stringify(draft) !== JSON.stringify(serverValue),
  };
}

export function ReadOnlyNotice({ readOnly }: { readOnly: boolean }): React.JSX.Element | null {
  if (!readOnly) return null;
  return (
    <Alert>
      <Lock className="h-4 w-4" />
      <AlertTitle>Read-only</AlertTitle>
      <AlertDescription>
        Only a Super Admin can change these settings. You can see the current values so you know what
        generation will use.
      </AlertDescription>
    </Alert>
  );
}

interface SettingSliderProps {
  id: string;
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  format?: (value: number) => string;
  onChange: (value: number) => void;
}

/** SPEC §8 — every numeric field is a bounded slider; free-text config is forbidden. */
export function SettingSlider({
  id,
  label,
  description,
  value,
  min,
  max,
  step,
  disabled = false,
  format,
  onChange,
}: SettingSliderProps): React.JSX.Element {
  return (
    <div className={cn('space-y-2', disabled && 'opacity-70')}>
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        <span className="text-sm tabular-nums text-muted-foreground">
          {format ? format(value) : value}
        </span>
      </div>
      <Slider
        id={id}
        aria-label={label}
        value={[value]}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onValueChange={([next]) => onChange(next)}
      />
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}
